interface ErrorReport {
  message: string;
  stack?: string;
  timestamp: number;
  level: 'error' | 'warning' | 'info';
  context?: Record<string, unknown>;
}

class ErrorTracker {
  private queue: ErrorReport[] = [];
  private dsn: string | null = null;

  init(dsn?: string): void {
    this.dsn = dsn ?? null;

    // Catch unhandled errors
    window.addEventListener('error', (event) => {
      this.captureError(event.error ?? new Error(event.message), 'error', {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    });

    // Catch unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.captureError(
        event.reason instanceof Error ? event.reason : new Error(String(event.reason)),
        'error',
        { type: 'unhandled_rejection' },
      );
    });
  }

  captureError(error: Error, level: 'error' | 'warning' | 'info' = 'error', context?: Record<string, unknown>): void {
    const report: ErrorReport = {
      message: error.message,
      stack: error.stack,
      timestamp: Date.now(),
      level,
      context,
    };

    this.queue.push(report);

    // Log to console in development
    if (import.meta.env.DEV) {
      console.error(`[ErrorTracker] ${level}:`, error.message, context);
    }

    // Flush periodically
    if (this.queue.length >= 5) {
      this.flush();
    }
  }

  captureMessage(message: string, level: 'error' | 'warning' | 'info' = 'info'): void {
    this.queue.push({
      message,
      timestamp: Date.now(),
      level,
    });
  }

  flush(): void {
    if (this.queue.length === 0) return;

    if (this.dsn) {
      // In production, send to Sentry or other error service
      console.log('[ErrorTracker] Flushing errors:', this.queue.length);
    }

    this.queue = [];
  }

  getQueue(): ErrorReport[] {
    return [...this.queue];
  }
}

export const errorTracker = new ErrorTracker();
